import { Random } from '@educraft/base';
import { PdfRect, PdfSize, PdfPoint } from '../pdf/pdf';

export interface PlacedObject<T> {
  data: T;
  rect: PdfRect;
}

export interface PlaceObjectsOptions {
  maxAttempts?: number;
  maxTries?: number;
  minSpacing?: number;
}

/**
 * Randomly place objects within a container without overlapping.
 * Port of C# G<T>.PlaceObjects()
 */
export function placeObjects<T>(
  random: Random,
  container: PdfRect,
  size: PdfSize,
  items: T[],
  options?: PlaceObjectsOptions
): PlacedObject<T>[] {
  const maxAttempts = options?.maxAttempts ?? 10000;
  const maxTries = options?.maxTries ?? 100;
  const minSpacing = options?.minSpacing ?? 0;

  let result: PlacedObject<T>[] = [];
  let attempts = 0;

  do {
    result = [];
    attempts++;

    for (const item of items) {
      const placed = placeObject(random, result, container, size, item, maxTries, minSpacing);
      if (placed) {
        result.push(placed);
      }
    }

    if (attempts > maxAttempts) {
      break;
    }
  } while (result.length < items.length);

  return result;
}

function placeObject<T>(
  random: Random,
  placedObjects: PlacedObject<T>[],
  container: PdfRect,
  size: PdfSize,
  item: T,
  maxTries: number,
  minSpacing: number
): PlacedObject<T> | null {
  for (let i = 0; i < maxTries; i++) {
    const x = container.x + random.nextDouble() * (container.width - size.width);
    const y = container.y + random.nextDouble() * (container.height - size.height);
    const rect = new PdfRect(x, y, size.width, size.height);

    let overlaps = false;
    for (const placed of placedObjects) {
      if (rectsOverlap(rect, placed.rect, minSpacing)) {
        overlaps = true;
        break;
      }
    }

    if (!overlaps) {
      return { data: item, rect };
    }
  }

  return null;
}

function rectsOverlap(a: PdfRect, b: PdfRect, minSpacing: number): boolean {
  if (minSpacing <= 0) {
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
  }

  const inf1 = inflateRect(a, minSpacing);
  const inf2 = inflateRect(b, minSpacing);

  return !(
    inf1.right <= inf2.left ||
    inf1.left >= inf2.right ||
    inf1.bottom <= inf2.top ||
    inf1.top >= inf2.bottom
  );
}

function inflateRect(rect: PdfRect, amount: number): PdfRect {
  return new PdfRect(
    rect.x - amount,
    rect.y - amount,
    rect.width + 2 * amount,
    rect.height + 2 * amount
  );
}
